import * as React from 'react';
import { Box, TextField, Button } from '@mui/material';

interface MfaEntryProps {
  onCodeEntered: (code: string) => Promise<void> | void;
  loading?: boolean; // external loading state (e.g., parent awaiting confirmation)
}

export default function MfaEntry(props: MfaEntryProps) {

  const [code, setCode] = React.useState<string[]>(Array(6).fill(''));
  const [submitting, setSubmitting] = React.useState(false);
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);
  const expectedLength = 6;
  
  const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    
    const newCode = [...code];
    const value = e.target.value;
  
    // Ignore non-numeric input
    if (value === '' || /^[0-9]+$/.test(value)) {
      newCode[index] = value;
      setCode(newCode);
  
      // Move to the next input
      if (value.length === 1 && index < refs.current.length - 1) {
        refs.current[index + 1]?.focus();
      }

    }
  };

  // Paste handler: allow user to paste entire code into any field
  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('Text').trim();
    if (/^[0-9]{6}$/.test(text)) {
      e.preventDefault();
      setCode(text.split(''));
      // Focus last box for clarity
      refs.current[expectedLength - 1]?.focus();
    }
  };

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
      refs.current[index - 1]?.focus();
    } else if (e.key === 'Enter' && code.join('').length === 6) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (submitting || props.loading) return; // guard against duplicate triggers
    const c = code.join('');
    if (c.length !== expectedLength || code.some(d => d.length !== 1)) return;
    setSubmitting(true);
    try {
      await props.onCodeEntered(c);
    } finally {
      // Don't clear if parent sets persistent loading; leave digits in place until external loading ends
      if (!props.loading) {
        setCode(Array(expectedLength).fill(''));
        setSubmitting(false);
        refs.current[0]?.focus();
      }
    }
  };

  // Auto-submit temporarily disabled; keep logic commented for easy restore.
  // React.useEffect(() => {
  //   if (!submitting && code.every(d => d.length === 1) && code.join('').length === expectedLength) {
  //     handleSubmit();
  //   }
  // }, [code, submitting]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'auto', mb: 2 }} onPaste={handlePaste}>
      <Box sx={{ display: 'flex', gap: 1}}>
        {code.map((digit, index) => (
          <TextField
            key={index}
            variant="outlined"
            value={digit}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
            sx={{ width: '45px', display: 'flex', alignItems: 'center' }}
            inputRef={ref => refs.current[index] = ref} />
        ))}
      </Box>
      <Button 
        variant="contained" color="primary" 
        sx={{ mt: 6, position: 'relative', minWidth: '120px' }}
        disabled={submitting || props.loading || code.some(digit => digit.length === 0)}
        onClick={handleSubmit} >
        {(submitting || props.loading) ? 'Submitting…' : 'Submit'}
      </Button>
    </Box>
  );
}
